import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useConfirmService } from '../../../services/confirmService';
import apiService from '../../../services/api';
import CustomDropdown, { DropdownOption } from '../../UI/CustomDropdown';
import SidePanel from '../../UI/SidePanel';
import EmptyState from '../../UI/EmptyState';
import { UserPermission } from '../../../types';
import styles from '../../../styles/GroupsTab.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import searchStyles from '../../../styles/SearchBar.module.css';
import sidePanelStyles from '../../../styles/SidePanel.module.css';
import customDropdownStyles from '../../../styles/CustomDropdown.module.css';

interface Group {
  id: string;
  name: string;
  description: string;
  member_count: number;
  task_count: number;
  points: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  logo?: string;
  team_lead?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  color?: string;
}

interface FilterState {
  search: string;
  status: string;
}

const GroupsTab: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const notificationService = useNotification();
  const confirmService = useConfirmService();

  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalGroups, setTotalGroups] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);

  // Permission states
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canAddGroup, setCanAddGroup] = useState(false);

  const GROUPS_PER_PAGE = 12;

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: ''
  });

  const [addForm, setAddForm] = useState({
    name: '',
    description: '',
    status: 'active',
    team_lead_id: ''
  });

  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    status: 'active',
    team_lead_id: ''
  });

  const [teamLeads, setTeamLeads] = useState<DropdownOption[]>([]);
  const [loadingTeamLeads, setLoadingTeamLeads] = useState(false);
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [isUpdatingGroup, setIsUpdatingGroup] = useState(false);

  // Load user permissions for group_management module
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user) {
        setPermissionsLoading(false);
        return;
      }

      try {
        setPermissionsLoading(true);
        const response = await apiService.getUserPermissions() as any;
        if (response && response.status === 'success') {
          const responseData = response.data || response;
          const permissions = (responseData.permissions || []) as UserPermission[];
          setUserPermissions(permissions);

          const hasAdd = permissions.some(
            perm => perm.module === 'group_management' && ['add', 'create'].includes(perm.action)
          );

          setCanAddGroup(hasAdd || (user as any).is_main_admin || user.role === 'admin');
        } else {
          setUserPermissions([]);
          setCanAddGroup((user as any).is_main_admin || user.role === 'admin');
        }
      } catch (error) {
        console.error('Error loading user permissions for group management:', error);
        setUserPermissions([]);
        setCanAddGroup((user as any).is_main_admin || user.role === 'admin');
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadUserPermissions();
  }, [user]);

  // Load team leads for dropdown
  const loadTeamLeads = async () => {
    if (loadingTeamLeads) return;

    setLoadingTeamLeads(true);
    try {
      const response = await apiService.getUsersList({
        page: 1,
        per_page: 1000, // Get all users for team lead selection
        status: 'active' // Only active users
      });

      if (response.status === 'success' && response.data) {
        const options: DropdownOption[] = response.data.map((user: any) => ({
          value: user.id,
          label: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown User'
        }));
        setTeamLeads(options);
      }
    } catch (error: any) {
      console.error('Error loading team leads:', error);
      notificationService.error('Failed to load team leads. Please try again.');
    } finally {
      setLoadingTeamLeads(false);
    }
  };

  // Load groups from API - simplified like UserManagement
  const loadGroups = async (page: number = 1) => {
    // Prevent multiple simultaneous calls
    if (isLoadingGroups) {
      return;
    }

    setIsLoadingGroups(true);
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getGroupsList({
        page: page,
        per_page: GROUPS_PER_PAGE,
        search: filters.search,
        status: filters.status
      });

      if (response.status === 'success') {
        setGroups(response.data || []);
        setTotalGroups(response.total || 0);
        setTotalPages(response.num_pages || 1);
        setCurrentPage(page);
      } else {
        setError(response.message || 'Failed to load groups');
        notificationService.error(response.message || 'Failed to load groups');
      }
    } catch (err: any) {
      console.error('Error loading groups:', err);
      setError(err);
      notificationService.error(err.response?.data?.message || 'Failed to load groups. Please try again.');
    } finally {
      setLoading(false);
      setIsLoadingGroups(false);
    }
  };

  // Handle filter updates
  const updateFilters = (updates: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...updates }));
    setCurrentPage(1);
  };

  // Handle search
  const handleSearch = (value: string) => {
    updateFilters({ search: value });
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof FilterState, value: string) => {
    updateFilters({ [key]: value });
  };

  // Open add modal
  const openAddModal = () => {
    setAddForm({
      name: '',
      description: '',
      status: 'active',
      team_lead_id: ''
    });
    loadTeamLeads();
    setShowAddModal(true);
  };

  const openEditModal = (group: Group) => {
    setEditingGroup(group);
    setEditForm({
      name: group.name,
      description: group.description,
      status: group.is_active ? 'active' : 'inactive',
      team_lead_id: group.team_lead?.id || ''
    });
    loadTeamLeads();
    setShowEditModal(true);
  };

  const handleAddGroup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isAddingGroup) return;

    if (!addForm.team_lead_id) {
      notificationService.error('Please select a team lead');
      return;
    }

    setIsAddingGroup(true);
    try {
      showLoader();
      const response = await apiService.addGroup({
        name: addForm.name,
        description: addForm.description,
        is_active: addForm.status === 'active',
        team_lead_id: addForm.team_lead_id
      });

      if (response.status === 'success') {
        notificationService.success('Group created successfully');
        setShowAddModal(false);
        setAddForm({ name: '', description: '', status: 'active', team_lead_id: '' });
        loadGroups(1);
      } else {
        notificationService.error(response.message || 'Failed to create group');
      }
    } catch (error: any) {
      console.error('Error adding group:', error);
      notificationService.error(error.response?.data?.message || 'Failed to create group. Please try again.');
    } finally {
      setIsAddingGroup(false);
      hideLoader();
    }
  };

  const handleEditGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGroup || isUpdatingGroup) return;

    if (!editForm.team_lead_id) {
      notificationService.error('Please select a team lead');
      return;
    }

    setIsUpdatingGroup(true);
    try {
      showLoader();
      const updateData: any = {
        name: editForm.name,
        description: editForm.description,
        is_active: editForm.status === 'active'
      };

      if (editForm.team_lead_id) {
        updateData.team_lead_id = editForm.team_lead_id;
      }

      const response = await apiService.updateGroup(editingGroup.id, updateData);

      if (response.status === 'success') {
        notificationService.success('Group updated successfully');
        setShowEditModal(false);
        setEditingGroup(null);
        loadGroups(currentPage);
      } else {
        notificationService.error(response.message || 'Failed to update group');
      }
    } catch (error: any) {
      console.error('Error updating group:', error);
      notificationService.error(error.response?.data?.message || 'Failed to update group. Please try again.');
    } finally {
      setIsUpdatingGroup(false);
      hideLoader();
    }
  };

  // Handle delete group
  const handleDeleteGroup = async (group: Group) => {
    const confirmed = await confirmService.show(
      'Delete Group',
      `Are you sure you want to delete "${group.name}"? This action cannot be undone.`,
      { okText: 'Delete', cancelText: 'Cancel' }
    );

    if (confirmed) {
      try {
        showLoader();
        const response = await apiService.deleteGroup(group.id);
        if (response.status === 'success') {
          notificationService.success('Group deleted successfully');
          if (groups.length === 1 && currentPage > 1) {
            loadGroups(currentPage - 1);
          } else {
            loadGroups(currentPage);
          }
        } else {
          notificationService.error(response.message || 'Failed to delete group');
        }
      } catch (error: any) {
        console.error('Error deleting group:', error);
        notificationService.error(error.response?.data?.message || 'Failed to delete group. Please try again.');
      } finally {
        hideLoader();
      }
    }
  };

  // Handle toggle group status
  const handleToggleStatus = async (group: Group) => {
    const action = group.is_active ? 'deactivate' : 'activate';
    const confirmed = await confirmService.show(
      `${action.charAt(0).toUpperCase() + action.slice(1)} Group`,
      `Are you sure you want to ${action} "${group.name}"?`,
      { okText: action.charAt(0).toUpperCase() + action.slice(1), cancelText: 'Cancel' }
    );

    if (confirmed) {
      try {
        showLoader();
        const response = await apiService.updateGroup(group.id, {
          is_active: !group.is_active
        });

        if (response.status === 'success') {
          notificationService.success(`Group ${action}d successfully`);
          loadGroups(currentPage);
        } else {
          notificationService.error(response.message || `Failed to ${action} group`);
        }
      } catch (error: any) {
        console.error(`Error ${action}ing group:`, error);
        notificationService.error(error.response?.data?.message || `Failed to ${action} group. Please try again.`);
      } finally {
        hideLoader();
      }
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadGroups(1);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters.search, filters.status]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const generateGradientFromColor = (baseColor: string) => {
    if (!baseColor || typeof baseColor !== 'string') {
      baseColor = '#4f46e5';
    }

    if (!baseColor.startsWith('#')) {
      baseColor = '#' + baseColor;
    }

    if (!/^#[0-9A-Fa-f]{6}$/.test(baseColor)) {
      baseColor = '#4f46e5';
    }

    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
      r /= 255;
      g /= 255;
      b /= 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0, s = 0, l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break;
          case g: h = (b - r) / d + 2; break;
          case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
      }
      return [h * 360, s * 100, l * 100];
    };

    const hslToRgb = (h: number, s: number, l: number) => {
      h /= 360;
      s /= 100;
      l /= 100;
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };
      let r, g, b;
      if (s === 0) {
        r = g = b = l;
      } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
      }
      return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    };

    const rgbToHex = (r: number, g: number, b: number) => {
      return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    };

    const [h, s, l] = rgbToHsl(r, g, b);
    const variations = [
      {
        h: h,
        s: Math.min(100, s + 10),
        l: Math.min(90, l + 15)
      },
      {
        h: h,
        s: Math.max(20, s - 5),
        l: Math.max(10, l - 20)
      }
    ];

    const startRgb = hslToRgb(variations[0].h, variations[0].s, variations[0].l);
    const endRgb = hslToRgb(variations[1].h, variations[1].s, variations[1].l);

    return {
      start: rgbToHex(startRgb[0], startRgb[1], startRgb[2]),
      end: rgbToHex(endRgb[0], endRgb[1], endRgb[2])
    };
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`${styles.statusBadge} ${isActive ? styles.active : styles.inactive}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const statusOptions: DropdownOption[] = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  return (
    <div className={styles.groupsTab}>
      {/* Fixed Header */}
      <div className={styles.fixedHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerRow}>
            <div className={styles.searchWrapper}>
              <div className={searchStyles.searchBar}>
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <button type="submit">
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </button>
              </div>
            </div>

            <div className={styles.headerActions}>
              {canAddGroup && !permissionsLoading && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                  onClick={openAddModal}
                  title="Add New Group"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12h14"></path>
                  </svg>
                  <span className={styles.btnText}>Add</span>
                </button>
              )}
              <CustomDropdown
                options={statusOptions}
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value as string)}
                placeholder="All Status"
                size="sm"
                variant="outlined"
                className="filter"
              />

              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                onClick={() => loadGroups(currentPage)}
                title="Refresh"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2v6h-6"></path>
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                  <path d="M3 22v-6h6"></path>
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
                </svg>
                <span className={styles.btnText}>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className={styles.contentSection}>
        {/* Groups Grid */}
        {loading ? (
          <div className={styles.groupsGrid}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className={styles.groupCardSkeleton}>
                <div className={styles.skeletonImage}>
                  <div className={styles.skeletonBadge}></div>
                </div>
                <div className={styles.skeletonContent}>
                  <div className={styles.skeletonHeader}>
                    <div className={styles.skeletonTitle}></div>
                    <div className={styles.skeletonDescription}></div>
                    <div className={styles.skeletonDescriptionShort}></div>
                  </div>
                  <div className={styles.skeletonMeta}>
                    <div className={styles.skeletonMetaItem}></div>
                    <div className={styles.skeletonMetaItem}></div>
                  </div>
                  <div className={styles.skeletonFooter}>
                    <div className={styles.skeletonDate}></div>
                    <div className={styles.skeletonButton}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : groups.length > 0 ? (
          <div className={styles.groupsGrid}>
            {groups.map((group) => {
              const palette = generateGradientFromColor(group.color || '#4f46e5');
              const statusText = group.is_active ? 'Active' : 'Inactive';
              const teamLeadName = group.team_lead
                ? `${group.team_lead.first_name || ''} ${group.team_lead.last_name || ''}`.trim() || group.team_lead.email || 'No Team Lead'
                : 'No Team Lead';
              const memberCount = group.member_count || 0;
              const createdDate = formatDate(group.created_at);

              return (
                <div
                  key={group.id}
                  className={styles.groupCard}
                  onClick={() => navigate(`/group-management/groups/${group.id}`)}
                >
                  <div
                    className={styles.groupImage}
                    style={{
                      '--gradient-start': palette.start,
                      '--gradient-end': palette.end
                    } as React.CSSProperties}
                  >
                    <div className={styles.floatingDots}></div>
                    <div className={styles.ribbon}></div>
                    <span className={styles.groupBadge}>{statusText}</span>
                  </div>

                  <div className={styles.groupContent}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div>
                        <h3 className={styles.groupTitle}>{group.name}</h3>
                        <p className={styles.groupDescription}>
                          {group.description || 'No description available for this group.'}
                        </p>
                      </div>
                      {group.logo && (
                        null
                      )}
                    </div>

                    <div className={styles.groupMeta}>
                      <span className={styles.groupCourse}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                        </svg>
                        {teamLeadName}
                      </span>
                      <span className={styles.groupDuration}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        {memberCount} Members
                      </span>
                    </div>

                    <div className={styles.groupFooter}>
                      <span className={styles.groupDate}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        {createdDate}
                      </span>
                      <button
                        className={styles.viewButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/group-management/groups/${group.id}`);
                        }}
                      >
                        View
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            title="No Groups Found"
            description={
              filters.search || filters.status
                ? 'No groups match your current filters. Try adjusting your search criteria.'
                : 'Get started by creating your first group.'
            }
            action={
              canAddGroup && !permissionsLoading
                ? {
                  label: 'Create First Group',
                  onClick: openAddModal
                }
                : undefined
            }
          />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <div className={styles.stepLinks}>
              {currentPage > 1 && (
                <button
                  className={styles.paginationBtn}
                  onClick={() => loadGroups(currentPage - 1)}
                >
                  Previous
                </button>
              )}

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`${styles.paginationBtn} ${page === currentPage ? styles.active : ''}`}
                  onClick={() => loadGroups(page)}
                >
                  {page}
                </button>
              ))}

              {currentPage < totalPages && (
                <button
                  className={styles.paginationBtn}
                  onClick={() => loadGroups(currentPage + 1)}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Group Side Panel */}
      <SidePanel
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Group"
        icon={
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        }
      >
        <form className={sidePanelStyles.form} onSubmit={handleAddGroup}>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Group Name <span className={sidePanelStyles.required}>*</span>
            </label>
            <input
              type="text"
              className={sidePanelStyles.formControl}
              value={addForm.name}
              onChange={(e) => setAddForm(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>

          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Description
            </label>
            <textarea
              className={sidePanelStyles.formControl}
              rows={3}
              value={addForm.description}
              onChange={(e) => setAddForm(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Team Lead <span className={sidePanelStyles.required}>*</span>
            </label>
            <CustomDropdown
              options={teamLeads}
              value={addForm.team_lead_id}
              onChange={(value) => setAddForm(prev => ({ ...prev, team_lead_id: value as string }))}
              placeholder={loadingTeamLeads ? "Loading..." : "Select Team Lead"}
              size="md"
              variant="outlined"
              className={customDropdownStyles.sizeFull}
              disabled={loadingTeamLeads}
              searchable={true}
            />
          </div>

          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Status
            </label>
            <CustomDropdown
              options={statusOptions.filter(opt => opt.value !== '')}
              value={addForm.status}
              onChange={(value) => setAddForm(prev => ({ ...prev, status: value as string }))}
              placeholder="Select Status"
              size="md"
              variant="outlined"
              className={customDropdownStyles.sizeFull}
              searchable={true}
            />
          </div>

          <div className={sidePanelStyles.formFooter}>
            <button
              type="button"
              className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
              onClick={() => setShowAddModal(false)}
              disabled={isAddingGroup}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
              disabled={isAddingGroup}
            >
              {isAddingGroup ? (
                <>
                  <svg className={styles.spinner} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Group'
              )}
            </button>
          </div>
        </form>
      </SidePanel>

      {/* Edit Group Side Panel */}
      <SidePanel
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Group"
        icon={
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        }
      >
        <form className={sidePanelStyles.form} onSubmit={handleEditGroup}>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Group Name <span className={sidePanelStyles.required}>*</span>
            </label>
            <input
              type="text"
              className={sidePanelStyles.formControl}
              value={editForm.name}
              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>

          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Description
            </label>
            <textarea
              className={sidePanelStyles.formControl}
              rows={3}
              value={editForm.description}
              onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Team Lead <span className={sidePanelStyles.required}>*</span>
            </label>
            <CustomDropdown
              options={teamLeads}
              value={editForm.team_lead_id}
              onChange={(value) => setEditForm(prev => ({ ...prev, team_lead_id: value as string }))}
              placeholder={loadingTeamLeads ? "Loading..." : "Select Team Lead"}
              size="md"
              variant="outlined"
              className={customDropdownStyles.sizeFull}
              disabled={loadingTeamLeads}
            />
          </div>

          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Status
            </label>
            <CustomDropdown
              options={statusOptions.filter(opt => opt.value !== '')}
              value={editForm.status}
              onChange={(value) => setEditForm(prev => ({ ...prev, status: value as string }))}
              placeholder="Select Status"
              size="md"
              variant="outlined"
            />
          </div>

          <div className={sidePanelStyles.formFooter}>
            <button
              type="button"
              className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
              onClick={() => setShowEditModal(false)}
              disabled={isUpdatingGroup}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
              disabled={isUpdatingGroup}
            >
              {isUpdatingGroup ? (
                <>
                  <svg className={styles.spinner} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Saving...
                </>
              ) : (
                'Update Group'
              )}
            </button>
          </div>
        </form>
      </SidePanel>
    </div>
  );
};

export default GroupsTab;